import { Badge, Switch, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch, FilterSelect } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { Tooltip } from '../../../../components/Tooltip'
import { formatDateTime, formatLocalDateTime, useHasRole, useAppToast, usePendingRows } from '../../../../helpers'
import { BlogPostService } from '../../../../services/BlogPost/BlogPostService'
import { BlogPost } from '../../../../services/BlogPost/types'
import { PostsFormModal } from './PostsFormModal'

/** `page_size` fica de fora: é o tamanho de página escolhido no Paginator, não um filtro. */
const INITIAL_FILTERS = { search: '', tag: '', status: '' }

export default function PostsPage() {
  const service = new BlogPostService()
  const [isDeleting, setIsDeleting] = useState(false)
  const [postToActivate, setPostToActivate] = useState<BlogPost | null>(null)
  const toast = useAppToast()
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [targetRecord, setTargetRecord] = useState<BlogPost>()
  const [filters, setFilters] = useState({ page_size: DEFAULT_PAGE_SIZE, ...INITIAL_FILTERS })
  const isDirty = filters.search !== '' || filters.tag !== '' || filters.status !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilters = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number>()
  const hasRole = useHasRole()
  const permissions = {
    addPost: hasRole('add_post'),
    editPost: hasRole('edit_post'),
    deletePost: hasRole('delete_post')
  }

  // Único efeito de busca: com o reset de página feito junto do `setFilters` em `updateFilters`,
  // as duas mudanças caem numa renderização só e isto roda uma vez. Voltar a página num efeito
  // separado fazia `refreshList` rodar antes com a página antiga, e de novo depois.
  useEffect(() => {
    refreshList()
  }, [filters, page])

  const handleDeletePost = async () => {
    if (!targetRecord) {
      return
    }

    setIsDeleting(true)
    try {
      await service.delete(targetRecord.id)
      toast.success('Post excluído')
      setOpenDeleteDialog(false)
      refreshList()
    } catch (error) {
      // Um 400 aqui é regra de negócio (o post está vinculado a algo), não falha de
      // servidor: a mensagem do backend explica o motivo e o diálogo fecha.
      if ((error as AxiosError)?.response?.status === 400) {
        toast.error('Não foi possível excluir o post', error)
        setOpenDeleteDialog(false)
        return
      }
      toast.error('Erro ao excluir post', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const refreshList = async () => {
    setLoading(true)
    setLoadError(false)
    service
      .list({ ...filters, page })
      .then((response) => {
        setBlogPosts(response.data)
        setTotalPages(Math.ceil(response.total_filtered / filters.page_size))
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar os posts', error)
      })
      .finally(() => setLoading(false))
  }

  // O modal busca o post completo sozinho e mostra o carregamento: buscar aqui também fazia
  // duas requisições iguais, e o clique ficava sem resposta visual até a primeira voltar.
  const openEditModal = (id: number) => {
    setEditingId(id)
    setIsCreateModalOpen(true)
  }

  const savingRow = usePendingRows()

  const handleToggleActive = async (post: BlogPost) => {
    // Ativar um post agendado descarta o agendamento, então pede confirmação antes.
    if (!post.is_active && post.scheduled_at) {
      setPostToActivate(post)
      return
    }

    await toggleActive(post)
  }

  const toggleActive = async (post: BlogPost) => {
    // O `return` importa: sem ele quem chama não tem o que aguardar, e o `usePendingRows`
    // liberaria o switch antes de a resposta chegar.
    return service
      .toggleActive(post.id)
      .then((response) => {
        setBlogPosts((prev) =>
          prev.map((p) => (p.id === post.id ? { ...p, is_active: response.is_active, scheduled_at: null } : p))
        )
      })
      .catch(() => {
        toast.error('Erro ao alterar status do post')
      })
  }

  const confirmActivate = () => {
    if (!postToActivate) {
      return
    }

    const post = postToActivate
    setPostToActivate(null)
    savingRow.run(post.id, () => toggleActive(post))
  }

  return (
    <>
      <FilterBar onClear={() => updateFilters(INITIAL_FILTERS)} isDirty={isDirty}>
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="Buscar por post"
        />
        <FilterSelect
          label="Tag"
          value={filters.tag}
          onChange={(tag) => updateFilters({ tag })}
          options={[
            { value: 'popular', label: 'Popular' },
            { value: 'normal', label: 'Normal' }
          ]}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => updateFilters({ status })}
          options={[
            { value: 'active', label: 'Ativo' },
            { value: 'inactive', label: 'Inativo' }
          ]}
        />
      </FilterBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Título</Th>
              <Th>Cabeçalho</Th>
              <Th>Popular</Th>
              <Th>Ativo</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="posts" onRetry={refreshList} />
            ) : blogPosts.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="post" />
            ) : (
              blogPosts.map((post) => (
                <Tr key={post.id}>
                  <Td>{post.id}</Td>
                  <Td maxW="200px">
                    <Tooltip label={post.title}>
                      <Text noOfLines={1}>{post.title}</Text>
                    </Tooltip>
                  </Td>
                  <Td maxW="200px">
                    <Tooltip label={post.header}>
                      <Text noOfLines={1}>{post.header}</Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Badge colorScheme={post.popular ? 'yellow' : 'gray'}>{post.popular ? 'Popular' : 'Normal'}</Badge>
                  </Td>
                  <Td>
                    <Tooltip
                      label={
                        post.scheduled_at
                          ? `Agendado: ${formatLocalDateTime(post.scheduled_at)}`
                          : post.is_active
                            ? 'Desativar'
                            : 'Ativar'
                      }
                    >
                      <span>
                        <Switch
                          isChecked={post.is_active}
                          onChange={() => savingRow.run(post.id, () => handleToggleActive(post))}
                          isDisabled={!permissions.editPost || savingRow.isPending(post.id)}
                        />
                      </span>
                    </Tooltip>
                  </Td>
                  <Td>{formatDateTime(post.created_at)}</Td>
                  <Td>
                    {permissions.editPost && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          openEditModal(post.id)
                        }}
                      />
                    )}
                    {permissions.deletePost && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setTargetRecord(post)
                          setOpenDeleteDialog(true)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={filters.page_size}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, page_size: pageSize })
          setPage(1)
        }}
      />

      {targetRecord && (
        <ConfirmDialog
          isOpen={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDeletePost}
          action="delete"
          entity="post"
          name={targetRecord?.title}
          isLoading={isDeleting}
        />
      )}

      <ConfirmDialog
        isOpen={!!postToActivate}
        onClose={() => setPostToActivate(null)}
        onConfirm={confirmActivate}
        action="save"
        title="Ativar post agendado?"
        description="Este post está agendado. Ao ativá-lo agora, o agendamento será descartado."
        confirmLabel="Ativar agora"
      />

      {permissions.addPost && (
        <ActionFab
          label="Adicionar post"
          onClick={() => {
            setEditingId(undefined)
            setIsCreateModalOpen(true)
          }}
        />
      )}

      <PostsFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSaved={refreshList}
        blogPostId={editingId}
      />
    </>
  )
}
